
import CustomFieldBoolean from "@/components/report/CustomFieldBoolean.vue";
import MHeader from "@/components/utility/mmmint/MHeader.vue";
import RefsFleet from "@/components/utility/RefsFleet.vue";
import RefsPerson from "@/components/utility/RefsPerson.vue";
import RefsVehicle from "@/components/utility/RefsVehicle.vue";
import TheLayoutPortal from "@/layouts/TheLayoutPortal.vue";
import { CreateReportAsPartnerDto } from "@/lib/dto/create-report-as-partner-dto";
import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { VehicleStateEnum } from "@/lib/enum/vehicleState.enum";
import { requiredRule } from "@/lib/rules/requiredRule";
import { handleError } from "@/lib/utility/handleError";
import { parsePhone } from "@/lib/utility/parsePhone";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { ActivityLog } from "@/models/activity-log.entity";
import { AttachmentRequest, IAttachmentRequest } from "@/models/attachment-request.entity";
import { Company } from "@/models/company.entity";
import { CreateSharedContentDto } from "@/models/create-shared-content.dto";
import { Fleet, IFleet } from "@/models/fleet.entity";
import { IPersonEmail, PersonEmail } from "@/models/person-email.entity";
import { IPersonPhone, PersonPhone } from "@/models/person-phone.entity";
import { IPerson } from "@/models/person.entity";
import { IReport } from "@/models/report.entity";
import { ISharedContent } from "@/models/shared-content.entity";
import { VehicleInsuranceContract, VehicleLeasingContract } from "@/models/vehicle-contract.entity";
import { IVehicle } from "@/models/vehicle.entity";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { CoverageTypeEnum } from "@/store/enum/coverage-type.enum";
import { DamageTypeEnum } from "@/store/enum/damage-type.enum";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { DamageTypeToCoverageTypeMap } from "@/store/mapper/damage-type-to-coverage-type.map";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import { RefTypeMap } from "@/store/modules/refs.store";
import { PersonSearchModule, VehicleSearchModule } from "@/store/modules/search.store";
import { VehicleModule } from "@/store/modules/vehicle.store";
import { debounce } from "debounce";
import { mixins } from "vue-class-component";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import CompanySheet from "../company/CompanySheet.vue";
import CreateVehicleDialog from "../fleet/CreateVehicleDialog.vue";
import PersonCreateDialog from "../person/PersonCreateDialog.vue";
import PersonEmailDialog from "../person/PersonEmailDialog.vue";
import PersonPhoneDialog from "../person/PersonPhoneDialog.vue";
import { CompanyServiceEnum } from "@/lib/enum/company-service.enum";

enum CreateReportSteps {
  VEHICLE = 1,
  DRIVER = 2,
  DAMAGE = 3,
  CONFIRM = 4
}

export type PartnerReportCreateFormSubmit = {
  report: IReport;
  sharedContent?: ISharedContent;
  attachmentRequest?: IAttachmentRequest;
  isNotifyUser: boolean;
  isShared: boolean;
  isAttachmentRequest: boolean;
};

@Component({
  components: {
    TheLayoutPortal,
    MHeader,
    RefsPerson,
    RefsVehicle,
    RefsFleet,
    CustomFieldBoolean,
    CreateVehicleDialog,
    PersonCreateDialog,
    CompanySheet,
    PersonEmailDialog,
    PersonPhoneDialog
  }
})
export default class PartnerReportCreateForm extends mixins(Vue, PermissionMixin) {
  DamageTypeEnum = DamageTypeEnum;
  CoverageTypeEnum = CoverageTypeEnum;
  CreateReportSteps = CreateReportSteps;

  isDamageValid = false;
  isDriverValid = false;

  @Prop({ required: false })
  groupId?: string;

  @Prop({ required: false })
  vehicle?: IVehicle;

  loading = false;

  isCreateVehicleDialogActive = false;
  isCreatePersonDialogActive = false;
  isCreateMailDialogActive = false;
  isUpdateMailDialogActive = false;
  isCreatePhoneDialogActive = false;
  isUpdatePhoneDialogActive = false;

  // #region data, non-ui
  currentStep = CreateReportSteps.VEHICLE;
  numberplate = "";

  // Change the type definitions to be more flexible
  damageType: DamageTypeEnum | null = null;
  coverageType: CoverageTypeEnum | null = null;
  message: string | null = null;
  isDrivable = true;
  isRoadworthy = true;

  // Vehicle search properties
  vehicleSearchPattern = "";
  isSearchingVehicles = false;
  selectedVehicle: IVehicle | null = this.vehicle || null;

  selectedFleet: IFleet | null = null;

  insuranceContract?: VehicleInsuranceContract | null = null;
  leasingContract?: VehicleLeasingContract | null = null;

  // Person search properties
  personSearchPattern = "";
  isSearchingPersons = false;
  selectedPerson: IPerson | null = null;

  debounceTimeout = 300;

  phoneSearch = "";
  phone: IPersonPhone | null = null;

  phoneToEdit: IPersonPhone = new PersonPhone();
  phoneUpdateIndex: number | null = null;
  isPhoneEditLoading = false;

  mailSearch = "";
  mail: IPersonEmail | null = null;

  mailToEdit: IPersonEmail = new PersonEmail();
  mailUpdateIndex: number | null = null;
  isMailEditLoading = false;

  isFleetDescriptionExpanded = false;

  startCreateEmail() {
    this.mailToEdit = new PersonEmail({ email: this.mailSearch });
    this.isCreateMailDialogActive = true;
  }

  startUpdateEmail(item: IPersonEmail) {
    const idx =
      this.selectedPerson?.emails.findIndex(e => (e.email || "").toLowerCase() === (item.email || "").toLowerCase()) ??
      -1;

    if (idx < 0) return;

    this.mailUpdateIndex = idx;

    this.mailToEdit = new PersonEmail({ ...this.selectedPerson?.emails[idx] });
    this.isUpdateMailDialogActive = true;
  }

  async updateEmail(updated: IPersonEmail) {
    if (this.mailUpdateIndex == null || !this.selectedPerson) return;

    this.isMailEditLoading = true;
    try {
      this.$set(this.selectedPerson.emails, this.mailUpdateIndex, updated);

      await this.selectedPerson.update();
      this.mail = this.selectedPerson.emails[this.mailUpdateIndex] || null;
    } catch (e) {
      handleError(e);
    } finally {
      this.isMailEditLoading = false;
    }
  }

  findExistingEmail(str: string) {
    const s = (str || "").trim().toLowerCase();

    return this.selectedPerson?.emails?.find(e => (e.email || "").toLowerCase() === s) || null;
  }

  onMailEnter() {
    const s = (this.mailSearch || "").trim();
    if (!s) return;

    const existing = this.findExistingEmail(s);
    if (existing) {
      this.mail = existing;

      return;
    }

    this.startCreateEmail();
  }

  findExistingPhone(str: string) {
    const s = parsePhone(str || "");

    return this.selectedPerson?.phones?.find(e => parsePhone(e.phone || "") === s) || null;
  }

  startUpdatePhone(item: IPersonPhone) {
    const idx =
      this.selectedPerson?.phones.findIndex(e => (e.phone || "").toLowerCase() === (item.phone || "").toLowerCase()) ??
      -1;

    if (idx < 0) return;

    this.phoneUpdateIndex = idx;

    this.phoneToEdit = new PersonPhone({ ...this.selectedPerson?.phones[idx] });
    this.phoneToEdit.phone = parsePhone(this.selectedPerson?.phones[idx]?.phone || "");
    this.isUpdatePhoneDialogActive = true;
  }

  async updatePhone(updated: IPersonPhone) {
    if (this.phoneUpdateIndex == null || !this.selectedPerson) return;

    this.isPhoneEditLoading = true;
    try {
      this.$set(this.selectedPerson.phones, this.phoneUpdateIndex, updated);

      await this.selectedPerson.update();
      this.phone = this.selectedPerson.phones[this.phoneUpdateIndex] || null;
    } catch (e) {
      handleError(e);
    } finally {
      this.isPhoneEditLoading = false;
    }
  }

  onPhoneEnter() {
    const s = (this.phoneSearch || "").trim();
    if (!s) return;

    const existing = this.findExistingPhone(s);
    if (existing) {
      this.phone = existing;

      return;
    }

    this.startCreatePhone();
  }

  startCreatePhone() {
    this.phoneToEdit = new PersonPhone();
    this.phoneToEdit.phone = this.phoneSearch;
    this.isCreatePhoneDialogActive = true;
  }

  async addPhone() {
    this.isPhoneEditLoading = true;

    try {
      this.selectedPerson?.phones.unshift(this.phoneToEdit);
      await this.selectedPerson?.update();

      this.phone = this.selectedPerson?.phones[0] || null;
    } catch (error) {
      handleError(error);
    } finally {
      this.isPhoneEditLoading = false;
    }
  }

  async addEmail() {
    this.isMailEditLoading = true;

    try {
      this.selectedPerson?.emails.unshift(this.mailToEdit);
      await this.selectedPerson?.update();

      this.mail = this.selectedPerson?.emails[0] || null;
    } catch (error) {
      handleError(error);
    } finally {
      this.isMailEditLoading = false;
    }
  }

  isNotifyUser = true;
  isShared = true;
  isAttachmentRequest = true;
  isMapRequest = false;

  screens: ReportScreenEnum[] = this.selectableScreens;

  get BackendResourceEnum() {
    return BackendResourceEnum;
  }

  get RefTypeMap() {
    return RefTypeMap;
  }

  get stateMap() {
    return VehicleModule.stateMap;
  }

  get drivable() {
    return this.isDrivable ? this.stateMap.get(VehicleStateEnum.ACTIVE) : this.stateMap.get(VehicleStateEnum.INACTIVE);
  }

  stepCompletion = {
    step1: false,
    step2: false,
    step3: false,
    step4: false
  };

  get partnerId() {
    return this.partner?._id || this.partner.id;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get filteredVehicles() {
    return VehicleSearchModule.filtered || [];
  }

  get filteredPersons() {
    return PersonSearchModule.filtered || [];
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get userId() {
    return UserModule.userId;
  }

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  get selectableScreens() {
    return this.removeNonSelectableScreens(
      (PartnerModule.partner.settings?.reportSettings.screenOrder ?? []) as ReportScreenEnum[]
    );
  }

  @Watch("vehicle")
  setSelectedVehicle() {
    if (this.selectedVehicle?.id !== this.vehicle?.id) {
      this.selectedVehicle = this.vehicle || null;
      this.checkStep1Completion();
    }
  }

  @Watch("selectedVehicle")
  emitSelectVehicle() {
    if (this.selectedVehicle?.id !== this.vehicle?.id) {
      this.$emit("update:vehicle", this.selectedVehicle);
    }
    this.checkStep1Completion();
  }

  removeNonSelectableScreens(screens: ReportScreenEnum[]) {
    return screens.filter(screen => !AttachmentRequest.INVALID_SCREENS.includes(screen));
  }

  setVehicle(vehicle: IVehicle) {
    this.$log.debug("Set new vehicle", vehicle);
    this.selectedVehicle = vehicle;
    this.numberplate = vehicle.numberplate || "";

    this.checkStep1Completion();
  }

  setPerson(person: IPerson) {
    this.$log.debug("Set new person", person);
    this.selectedPerson = person;

    this.checkStep2Completion();
  }

  mounted() {
    this.$nextTick(() => {
      const autocompleteElement = this.$refs.vehicleSearchAutocomplete as HTMLInputElement;
      if (autocompleteElement) {
        autocompleteElement.focus();
      }
    });
    this.checkStep1Completion();
  }

  removeScreen(item: ReportScreenEnum) {
    this.screens.splice(this.screens.indexOf(item), 1);
  }

  onEnterVehicleSearch() {
    if (this.selectedVehicle) {
      this.setStep(2);
    }
  }

  onEnterPersonSearch() {
    if (this.selectedPerson) {
      this.setStep(3);
    }
  }

  // Methods for step completion checks
  checkStep1Completion() {
    this.stepCompletion.step1 = Boolean(this.selectedVehicle);
  }

  checkStep2Completion() {
    this.stepCompletion.step2 = Boolean(this.selectedPerson);
  }

  // Update step completion check for step 3
  checkStep3Completion() {
    this.stepCompletion.step3 = Boolean(this.damageType) && Boolean(this.coverageType);
  }

  prev() {
    const nextStep = this.currentStep - 1;

    if (nextStep == CreateReportSteps.DRIVER && !this.canReadPerson) {
      return this.setStep(CreateReportSteps.VEHICLE);
    }

    this.setStep(nextStep);
  }

  next() {
    const nextStep = this.currentStep + 1;

    if (nextStep == CreateReportSteps.DRIVER && !this.canReadPerson) {
      return this.setStep(CreateReportSteps.DAMAGE);
    }

    this.setStep(nextStep);
  }

  // Method to handle step changes
  setStep(step: number) {
    this.currentStep = step;
  }

  // Vehicle search methods
  debounceVehicleSearch = debounce((val: string) => {
    this.vehicleSearchPattern = val;

    // Only perform backend search if we have at least 2 characters
    if (val && val.length >= 2) {
      this.searchVehicles();
    }
  }, this.debounceTimeout);

  async searchVehicles() {
    this.isSearchingVehicles = true;

    try {
      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.VEHICLE, this.partnerId)) {
        VehicleSearchModule.setSearch(this.vehicleSearchPattern);
        await VehicleSearchModule.fetchPage({ partnerId: this.partnerId });
      }
    } catch (error) {
      this.$toast?.error && this.$toast.error("Fehler beim Laden der Fahrzeuge");
      this.$log.error("Error searching vehicles:", error);
    } finally {
      this.isSearchingVehicles = false;
    }
  }

  // Person search methods
  debouncePersonSearch = debounce((val: string) => {
    this.personSearchPattern = val;
    this.searchPersons();
  }, this.debounceTimeout);

  async searchPersons() {
    // Only search if we have at least 2 characters
    if (!this.personSearchPattern || this.personSearchPattern.length < 2) {
      return;
    }

    this.isSearchingPersons = true;

    try {
      if (UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PERSON, this.partnerId)) {
        PersonSearchModule.setSearch(this.personSearchPattern);
        await PersonSearchModule.fetchPage({ partnerId: this.partnerId });
      }
    } catch (error) {
      this.$toast?.error && this.$toast.error("Fehler beim Laden der Personen");
      this.$log.error("Error searching persons:", error);
    } finally {
      this.isSearchingPersons = false;
    }
  }

  isLoadingFleet = false;

  async searchFleet() {
    if (!this.canReadFleet) {
      return;
    }
    if (this.selectedVehicle?.groupId) {
      try {
        this.isLoadingFleet = true;
        const fleet = new Fleet({ id: this.selectedVehicle.groupId, partnerId: this.partnerId });

        await fleet.fetch().catch(e => handleError(e));
        if (this.canReadCompany) {
          await fleet.fetchNextRootCompany().catch(e => handleError(e));
          await fleet.fetchCompanies().catch(e => handleError(e));
        }

        return fleet;
      } catch (e) {
        handleError(e);
      } finally {
        this.isLoadingFleet = false;
      }
    }
  }

  async setInsurance() {
    const insuranceContract = this.selectedVehicle?.activeInsuranceContract();
    if (insuranceContract && insuranceContract.companyId) {
      this.insuranceContract = insuranceContract || undefined;
      if (this.insuranceContract.companyId) {
        const company = await new Company({ id: insuranceContract.companyId, partnerId: this.partnerId })
          .fetch()
          .catch(e => handleError(e));
        if (company) {
          this.insuranceContract.company = company;
        }
      }
    } else {
      this.insuranceContract = undefined;
    }
  }

  async setLeasing() {
    const leasingContract = this.selectedVehicle?.activeLeasingContract();
    if (leasingContract && leasingContract.companyId) {
      this.leasingContract = leasingContract || undefined;
      if (this.leasingContract.companyId) {
        const company = await new Company({ id: leasingContract.companyId, partnerId: this.partnerId })
          .fetch()
          .catch(e => handleError(e));
        if (company) {
          this.leasingContract.company = company;
        }
      }
    } else {
      this.leasingContract = undefined;
    }
  }

  @Watch("selectedVehicle")
  async watchSelectedVehicle() {
    await this.setInsurance();
    await this.setLeasing();
    const fleet = await this.searchFleet();
    if (fleet) {
      this.selectedFleet = fleet;
    } else {
      this.selectedFleet = null;
      this.$log.debug(`no fleet found ${this.selectedVehicle}`);
    }
  }

  @Watch("selectedPerson")
  async watchSelectedPerson() {
    if (this.selectedPerson) {
      if (!this.selectedPerson.emails.length) {
        this.mail = null;
      } else if (this.selectedPerson.emails.length > 0) {
        this.mail = this.selectedPerson.emails[0];
      }
      if (!this.selectedPerson.phones.length) {
        this.phone = null;
      } else if (this.selectedPerson.phones.length > 0) {
        this.phone = this.selectedPerson.phones[0];
      }
    }
  }

  @Watch("damageType")
  watchDamageType() {
    if (this.damageType && !this.coverageType) {
      // Get corresponding coverage type enum value
      const coverageTypeEnum = DamageTypeToCoverageTypeMap.get(this.damageType) ?? null;

      if (coverageTypeEnum) {
        // Create a matching object structure for the coverageType
        this.coverageType = coverageTypeEnum;
      }

      this.checkStep3Completion();
    }
  }

  async submitReport() {
    const partnerId = this.partnerId;

    if (!this.selectedVehicle) {
      this.$toast.error(this.$t("components.partner.PartnerReportCreateForm.errors.selectVehicle"));

      throw new Error("No vehicle");
    }

    if (this.canReadPerson && !this.selectedPerson) {
      this.$toast.error(this.$t("components.partner.PartnerReportCreateForm.errors.selectPerson"));
      throw new Error("No person");
    }

    if (this.canReadPerson && !this.mail) {
      this.$toast.error(this.$t("components.partner.PartnerReportCreateForm.errors.email"));
      throw new Error("No Mail");
    }

    this.loading = true;

    const reportDto = CreateReportAsPartnerDto.createFromVehicle(this.selectedVehicle);
    reportDto.firstName = this.selectedPerson?.firstName;
    reportDto.lastName = this.selectedPerson?.lastName;

    if (this.canReadPerson) {
      reportDto.customerContact = {
        email: this.mail?.email || "",
        phone: this.phone?.phone || ""
      };
    } else {
      reportDto.customerContact = undefined;
    }

    reportDto.damageType = this.damageType ?? undefined;

    reportDto.coverageType = this.coverageType ?? undefined;

    reportDto.message = this.message ?? undefined;
    reportDto.isDrivable = this.isDrivable ?? undefined;
    reportDto.isRoadworthy = this.isRoadworthy;

    reportDto.assignees = [this.userId];
    reportDto.personId = this.selectedPerson?.id;

    /**
     * set companies
     */
    reportDto.companyId = this.selectedFleet?.company?.id || this.selectedFleet?.companyId;
    reportDto.companyName = this.selectedFleet?.company?.companyName;

    reportDto.insuranceCompanyId =
      this.insuranceContract?.companyId ||
      this.selectedFleet?.getCompanyByServiceEnum(CompanyServiceEnum.INSURANCE)?.id ||
      undefined;

    reportDto.leasingCompanyId =
      this.leasingContract?.companyId ||
      this.selectedFleet?.getCompanyByServiceEnum(CompanyServiceEnum.LEASING)?.id ||
      undefined;

    reportDto.insuranceBrokerCompanyId = this.selectedFleet?.getCompanyByServiceEnum(
      CompanyServiceEnum.INSURANCE_BROKER
    )?.id;
    reportDto.attorneyCompanyId = this.selectedFleet?.getCompanyByServiceEnum(CompanyServiceEnum.LAWYER)?.id;

    if (this.insuranceContract && this.insuranceContract.company?.companyName) {
      reportDto.insurances = [
        {
          name: this.insuranceContract.company?.companyName || "",
          number: ""
        }
      ];
    }

    if (this.leasingContract && this.leasingContract.company?.companyName) {
      reportDto.leasing = {
        isLeasing: true,
        company: this.leasingContract?.company?.companyName || "",
        claimNumber: ""
      };
    }

    reportDto.groupId = this.groupId;

    try {
      const created = await reportDto.create(partnerId);
      created.person = this.selectedPerson || null;

      this.$emit("created", created);

      const submit: PartnerReportCreateFormSubmit = {
        report: created,
        isNotifyUser: this.canReadPerson ? this.isNotifyUser : false,
        isShared: this.canReadPerson ? this.isShared : false,
        isAttachmentRequest: this.canReadPerson ? this.isAttachmentRequest : false
      };

      if (this.isShared) {
        const createSharedContentDto = new CreateSharedContentDto();
        createSharedContentDto.reportId = created.id;
        createSharedContentDto.title = `${this.$t("sharedContent.sharedContent").toString()} ${created.claimNumber} ${
          created.title
        }`;

        if (this.selectedPerson?.id) {
          createSharedContentDto.personIds?.push(this.selectedPerson?.id);
        }

        if (this.mail?.email) {
          createSharedContentDto.emailAddresses?.push(this.mail.email);
        }

        createSharedContentDto.refs = [
          {
            refType: BackendResourceEnum.REPORT,
            refId: created._id
          }
        ];

        if (this.isAttachmentRequest) {
          const attachmentRequest = await new AttachmentRequest({
            partnerId: this.partnerId,
            title: `${this.$t("objects.attachmentRequest.title").toString()} ${created.claimNumber} ${created.title}`,
            emailAddresses: [created.customerContact.email],
            personIds: created.personId ? [created.personId] : undefined,
            refs: [
              {
                refType: BackendResourceEnum.REPORT,
                refId: created._id
              }
            ],
            screenOrder: this.screens,
            reportId: created.id
          }).create();

          this.$log.debug(attachmentRequest.token);

          createSharedContentDto.refs.push({
            refType: BackendResourceEnum.ATTACHMENT_REQUEST,
            refId: attachmentRequest.id
          });

          submit.attachmentRequest = attachmentRequest;
        }

        const newShared = await createSharedContentDto.create();
        this.$emit("shared", newShared);

        if (newShared) {
          await new ActivityLog({
            partnerId: created.partnerId,
            source: {
              refType: ResourceEnum.REPORT,
              refId: created._id
            },
            target: [{ refType: ResourceEnum.SHARED_CONTENT, refId: newShared.id }],
            actionType: ActionEnum.CREATE,
            activity: ActivityTypeEnum.SHARE
          }).create();
        }

        this.$log.debug(newShared?.url);

        submit.sharedContent = newShared;
      }

      PartnerModule.setReport(created);

      this.$emit("submit", submit);
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  // Helper computed property to control the Next button
  get canProceedToNextStep() {
    switch (this.currentStep) {
      case CreateReportSteps.VEHICLE:
        return this.stepCompletion.step1;
      case CreateReportSteps.DRIVER:
        return this.stepCompletion.step2 && this.isDriverValid;
      case CreateReportSteps.DAMAGE:
        return this.stepCompletion.step3 && this.isDamageValid;
      default:
        return true;
    }
  }

  // Helper methods for the information panel
  getStepTitle(step: number) {
    switch (step) {
      case 1:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.selectVehicle");
      case 2:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.selectPerson");
      case 3:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.enterDamageDetails");
      case 4:
        return this.$t("components.partner.PartnerReportCreateForm.stepTitles.reviewAndSubmit");
      default:
        return "";
    }
  }

  // Add these computed properties for the transformed items
  get damageTypeItems() {
    return (this.partner.settings?.damageTypes ?? Object.values(DamageTypeEnum)).map(value => ({
      value,
      text: this.$t(`enums.DamageTypeEnum.${value}`)
    }));
  }

  get coverageTypeItems() {
    return Object.values(CoverageTypeEnum).map(value => ({
      value,
      text: this.$t(`enums.CoverageTypeEnum.${value}`)
    }));
  }

  // Custom filter function for searching through translated text
  customFilter(item: any, queryText: string) {
    if (!queryText) return true;

    const normalizedText = item.text.toLowerCase();
    const normalizedQuery = queryText.toLowerCase();

    return normalizedText.indexOf(normalizedQuery) > -1;
  }

  goToVehicle() {
    if (this.canReadVehicle) {
      this.selectedVehicle?.goTo(this.$router).detail();
    }
  }

  goToFleet() {
    if (this.canReadFleet) {
      this.selectedFleet?.goTo(this.$router).detail();
    }
  }
}
